import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../../../Context/AuthContext";
import axios from "axios";
import Table from "react-bootstrap/Table";
import { Authorization } from "../../../Context/Authorization";
import Preloader from "../../../shared/PreLoaders";

const LOCAL_KEY = "LoanAmountDataCache";

const saveToLocalCache = (data) => {
  localStorage.setItem(
    LOCAL_KEY,
    JSON.stringify({ ...data, timestamp: new Date().toISOString() })
  );
};

const loadFromLocalCache = () => {
  const cached = localStorage.getItem(LOCAL_KEY);
  return cached ? JSON.parse(cached) : null;
};

const formatDateTime = (timestamp) => {
  const date = new Date(timestamp);
  return date.toLocaleString("en-IN", { timeZone: "Asia/Kolkata" });
};
const RevolvingFundFirst = () => {
  const navigate = useNavigate();
  const { role } = useContext(AuthContext);
  const [circleData, setCircleData] = useState([]);
  const [divisionData, setDivisionData] = useState([]);
  const [rangeData, setRangeData] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState("");
  const [expandedCircles, setExpandedCircles] = useState({});
  const [expandedDivision, setExpandedDivision] = useState({});
  const [expandedRanges, setExpandedRanges] = useState({});

  const Api = process.env.REACT_APP_API;

  const fetchDataByRole = async () => {
    setLoading(true);
    try {
      const config = Authorization();
      if (role === "Admin") {
        const [circleRes, divisionRes, rangeRes] = await Promise.all([
          axios.get(`${Api}/api/drill/loan/circle-wise`, config),
          axios.get(`${Api}/api/drill/loan/division-wise`, config),
          axios.get(`${Api}/api/drill/loan/range-wise`, config),
        ]);
        const combinedData = {
          summary: {
            ...circleRes.data.summary,
            ...divisionRes.data.summary,
            ...rangeRes.data.summary,
          },
          circleData: circleRes.data.data,
          divisionData: divisionRes.data.data,
          rangeData: rangeRes.data.data,
        };
        setSummary(combinedData.summary);
        setCircleData(combinedData.circleData);
        setDivisionData(combinedData.divisionData);
        setRangeData(combinedData.rangeData);
        saveToLocalCache(combinedData);
      } else if (role === "Super Admin" || role === "CCF") {
        const res = await axios.get(
          `${Api}/api/drill/loan/circle-wise`,
          config
        );
        setSummary(res.data.summary);
        setCircleData(res.data.data);
        saveToLocalCache({
          summary: res.data.summary,
          circleData: res.data.data,
        });
      } else if (role === "DFO") {
        const res = await axios.get(
          `${Api}/api/drill/loan/division-wise`,
          config
        );
        setSummary(res.data.summary);
        setDivisionData(res.data.data);
        saveToLocalCache({
          summary: res.data.summary,
          divisionData: res.data.data,
        });
      } else if (role === "FRO") {
        const res = await axios.get(`${Api}/api/drill/loan/range-wise`, config);
        setSummary(res.data.summary);
        setRangeData(res.data.data);
        saveToLocalCache({
          summary: res.data.summary,
          rangeData: res.data.data,
        });
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const cached = loadFromLocalCache();
    if (cached) {
      setSummary(cached.summary || {});
      setCircleData(cached.circleData || []);
      setDivisionData(cached.divisionData || []);
      setRangeData(cached.rangeData || []);
      setLoading(false);
    } else {
      fetchDataByRole();
    }
  }, []);
  const formatAmount = (amount) => {
    if (amount >= 10000000) {
      return (amount / 10000000).toFixed(2) + " Cr"; // Crores
    } else if (amount >= 100000) {
      return (amount / 100000).toFixed(2) + " L"; // Lakhs
    } else if (amount >= 1000) {
      return (amount / 1000).toFixed(2) + " K"; // Thousands
    }
    return amount; // Return as is if less than 1000
  };
  const shouldShowCircleCard =
    role === "Admin" || role === "Super Admin" || role === "CCF";
  const shouldShowDivisionCard = role === "Admin" || role === "DFO";
  const shouldShowRangeCard = role === "Admin" || role === "FRO";

  if (loading) return <Preloader loading={true} />;

  return (
    <div>
      <div className="page-header">
        <h3 className="page-title"> </h3>
        <nav aria-label="breadcrumb">
          <ol className="breadcrumb">
            <li
              className="breadcrumb-item"
              style={{
                color: "blue",
                textDecoration: "underline",
                cursor: "pointer",
              }}
              onClick={() => navigate(-1)}
            >
              ← Back
            </li>
            <li
              className="breadcrumb-item active"
              style={{ color: "red", cursor: "pointer" }}
              onClick={() => navigate("/dashboard")}
            >
              🏠 Home
            </li>
            <li
              className="breadcrumb-item"
              style={{
                color: "blue",
                textDecoration: "underline",
                cursor: "pointer",
              }}
              onClick={() => navigate(+1)}
            >
              → Previous
            </li>
          </ol>
        </nav>
      </div>
      <div className="d-flex justify-content-between align-items-center mb-3 px-2">
        <button
          className="btn btn-sm btn-outline-info"
          onClick={() => {
            localStorage.removeItem(LOCAL_KEY);
            setLoading(true);
            fetchDataByRole();
          }}
        >
          🔄 Refresh Now
        </button>
        <div className="text-muted small">
          Last Refreshed:{" "}
          {loadFromLocalCache()?.timestamp
            ? formatDateTime(loadFromLocalCache().timestamp)
            : "Not Available"}
        </div>
      </div>
      {/* Card Row */}
      <div className="row">
        {/* Circle Card */}
        {shouldShowCircleCard && (
          <div className="col-md-4 stretch-card grid-margin mt-3">
            <div className="card bg-gradient-primary card-img-holder text-white">
              <div className="card-body">
                <img
                  src={require("../../../../assets/images/dashboard/circle.png")}
                  className="card-img-absolute"
                  alt="circle"
                />
                <h4 className="font-weight-normal mb-3">
                  Circle - Revolving Fund Loan
                </h4>
                <h3>
                  Loan Amount Recovered -{" "}
                  {formatAmount(summary.totalCircleRevolvingFundLoanAmount) ||
                    0}
                </h3>
              </div>
            </div>
          </div>
        )}

        {/* Division Card */}
        {shouldShowDivisionCard && (role === "DFO" || role === "Admin") && (
          <div className="col-md-4 stretch-card grid-margin mt-3">
            <div className="card bg-gradient-primary card-img-holder text-white">
              <div className="card-body">
                <img
                  src={require("../../../../assets/images/dashboard/circle.png")}
                  className="card-img-absolute"
                  alt="division"
                />
                <h4 className="font-weight-normal mb-3">
                  Division - Revolving Fund Loan
                </h4>
                <h3>
                  {summary.totalDivisionRevolvingFundLoanAmount.toLocaleString(
                    "en-IN"
                  ) || 0}{" "}
                  - Loan Amount Dispursed
                </h3>
              </div>
            </div>
          </div>
        )}

        {/* Range Card */}
        {shouldShowRangeCard && (role === "FRO" || role === "Admin") && (
          <div className="col-md-4 stretch-card grid-margin mt-3">
            <div className="card bg-gradient-primary card-img-holder text-white">
              <div className="card-body">
                <img
                  src={require("../../../../assets/images/dashboard/circle.png")}
                  className="card-img-absolute"
                  alt="range"
                />
                <h4 className="font-weight-normal mb-3">Range</h4>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="row mt-3">
        {shouldShowCircleCard && (
          <div className="col-md-4 mb-2">
            <button
              className={`btn btn-outline-primary m-1 ${
                viewMode === "All" ? "active" : ""
              }`}
              onClick={() => {
                setViewMode("All");
                setExpandedCircles({});
              }}
            >
              All Circles
            </button>
          </div>
        )}
        {shouldShowDivisionCard && (
          <div className="col-md-4 mb-2">
            <button
              className={`btn btn-outline-primary m-1 ${
                viewMode === "AllDivision" ? "active" : ""
              }`}
              onClick={() => {
                setViewMode("AllDivision");
                setExpandedDivision({});
              }}
            >
              All Divisions
            </button>
          </div>
        )}
        {shouldShowRangeCard && (
          <div className="col-md-4 mb-2">
            <button
              className={`btn btn-outline-primary m-1 ${
                viewMode === "AllRanges" ? "active" : ""
              }`}
              onClick={() => {
                setViewMode("AllRanges");
                setExpandedRanges({});
              }}
            >
              All Ranges
            </button>
          </div>
        )}
      </div>

      {/* Circles */}
      {viewMode === "All" &&
        circleData
          .sort((a, b) => a.circleName.localeCompare(b.circleName))
          .map((circle, idx) => (
            <div key={circle.circleId} className="mt-3">
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  cursor: "pointer",
                  background: "white",
                  padding: "10px",
                  borderRadius: "5px",
                }}
                onClick={() =>
                  setExpandedCircles((prev) => ({
                    ...prev,
                    [circle.circleId]: !prev[circle.circleId],
                  }))
                }
              >
                <h5 className="mb-0">{circle.circleName}</h5>
                <span style={{ fontSize: "1.2rem" }}>
                  {expandedCircles[circle.circleId] ? "−" : "+"}
                </span>
              </div>
              {expandedCircles[circle.circleId] && (
                <Table striped bordered hover className="mt-2 ">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Circle</th>
                      <th className="text-center">Male Beneficiaries</th>
                      <th className="text-center">Female Beneficiaries</th>
                      <th className="text-center">
                        Third Gender Beneficiaries
                      </th>
                      <th className="text-center">Amount Disbursed</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>{idx + 1}</td>
                      <td
                        style={{
                          color: "blue",
                          textDecoration: "underline",
                          cursor: "pointer",
                        }}
                        onClick={() =>
                          navigate(`/drill/circle/revolving/${circle.circleId}`)
                        }
                      >
                        {circle.circleName}
                      </td>

                      <td className="text-center">
                        {circle.revolvingFundLoan.male}
                      </td>
                      <td className="text-center">
                        {circle.revolvingFundLoan.female}
                      </td>
                      <td className="text-center">
                        {circle.revolvingFundLoan.others}
                      </td>
                      <td className="text-center">
                        {circle.revolvingFundLoanAmount.toLocaleString("en-IN")}
                      </td>
                    </tr>
                  </tbody>
                </Table>
              )}
            </div>
          ))}

      {/* Divisions */}
      {viewMode === "AllDivision" &&
        divisionData
          .sort((a, b) => a.divisionName.localeCompare(b.divisionName))
          .map((division, idx) => (
            <div key={division.divisionId} className="mt-3">
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  cursor: "pointer",
                  background: "white",
                  padding: "10px",
                  borderRadius: "5px",
                }}
                onClick={() =>
                  setExpandedDivision((prev) => ({
                    ...prev,
                    [division.divisionId]: !prev[division.divisionId],
                  }))
                }
              >
                <h5 className="mb-0">{division.divisionName}</h5>
                <span style={{ fontSize: "1.2rem" }}>
                  {expandedDivision[division.divisionId] ? "−" : "+"}
                </span>
              </div>
              {expandedDivision[division.divisionId] && (
                <Table striped bordered hover className="mt-2 ">
                  <thead>
                    <tr>
                      <th rowSpan="2">#</th>
                      <th>Division</th>
                      <th className="text-center">Male Beneficiaries</th>
                      <th className="text-center">Female Beneficiaries</th>
                      <th className="text-center">
                        Third Gender Beneficiaries
                      </th>
                      <th className="text-center">Amount Disbursed</th>
                    </tr>
                  </thead>

                  <tbody>
                    <tr>
                      <td>{idx + 1}</td>
                      <td
                        style={{
                          color: "blue",
                          textDecoration: "underline",
                          cursor: "pointer",
                        }}
                        onClick={() =>
                          navigate(
                            `/drill/division/revolving/${division.divisionId}`
                          )
                        }
                      >
                        {division.divisionName}
                      </td>

                      <td className="text-center">
                        {division.revolvingFundLoan.male}
                      </td>
                      <td className="text-center">
                        {division.revolvingFundLoan.female}
                      </td>
                      <td className="text-center">
                        {division.revolvingFundLoan.others}
                      </td>
                      <td className="text-center">
                        {division.revolvingFundLoanAmount.toLocaleString(
                          "en-IN"
                        )}
                      </td>
                    </tr>
                  </tbody>
                </Table>
              )}
            </div>
          ))}

      {/* Ranges */}
      {viewMode === "AllRanges" &&
        rangeData
          .sort((a, b) => a.rangeName.localeCompare(b.rangeName))
          .map((range, idx) => (
            <div key={range.rangeId} className="mt-3">
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  cursor: "pointer",
                  background: "white",
                  padding: "10px",
                  borderRadius: "5px",
                }}
                onClick={() =>
                  setExpandedRanges((prev) => ({
                    ...prev,
                    [range.rangeId]: !prev[range.rangeId],
                  }))
                }
              >
                <h5 className="mb-0">{range.rangeName}</h5>
                <span style={{ fontSize: "1.2rem" }}>
                  {expandedRanges[range.rangeId] ? "−" : "+"}
                </span>
              </div>
              {expandedRanges[range.rangeId] && (
                <Table striped bordered hover className="mt-2">
                  <thead>
                    <tr>
                      <th rowSpan="2">#</th>
                      <th>VFC</th>
                      <th className="text-center">Male Beneficiaries</th>
                      <th className="text-center">Female Beneficiaries</th>
                      <th className="text-center">
                        Third Gender Beneficiaries
                      </th>
                      <th className="text-center">Amount Disbursed</th>
                    </tr>
                  </thead>
                  <tbody>
                    {range.vfcs.map((vfc, index) => (
                      <tr>
                        <td>{index + 1}</td>
                        <td
                          style={{
                            color: "blue",
                            textDecoration: "underline",
                            cursor: "pointer",
                          }}
                          onClick={() =>
                            navigate(`/drill/vfc/revolving/${vfc.vfcId}`)
                          }
                        >
                          {vfc.vfcName}
                        </td>

                        <td className="text-center">
                          {vfc.revolvingFundLoan.male}
                        </td>
                        <td className="text-center">
                          {vfc.revolvingFundLoan.female}
                        </td>
                        <td className="text-center">
                          {vfc.revolvingFundLoan.others}
                        </td>
                        <td className="text-center">
                          {vfc.revolvingFundLoanAmount.toLocaleString("en-IN")}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </div>
          ))}
    </div>
  );
};

export default RevolvingFundFirst;
